/**
 * Decide whether a paste is a FILE paste, and pull the files out of it.
 *
 * TWIN: `Frontend/chat-widget/src/utils/clipboard.ts` is byte-for-byte the same logic. The two
 * apps are separate npm packages with no shared workspace and no tsconfig path mapping, so this
 * is a copy rather than an import. If you change the rules here, change them there — the whole
 * point is that a customer and an agent get identical paste behaviour.
 *
 * The hard part is not grabbing the files, it is knowing when NOT to. A paste handler that
 * swallows every event breaks ordinary text pasting, which is the overwhelmingly common case
 * and the one nobody forgives.
 *
 * Two rules, both conservative:
 *
 *  1. No files on the clipboard  -> not ours. Let the browser paste normally.
 *  2. Files AND non-empty text/plain -> not ours either. This is the copied-from-Word /
 *     copied-from-Excel shape: the clipboard carries a bitmap RENDERING alongside the real text
 *     payload. The user meant the text. Uploading a screenshot of their own spreadsheet instead
 *     of pasting its contents would be an obnoxious surprise.
 *
 * A genuine screenshot (PrtSc, Snipping Tool, Cmd-Shift-4) carries files and no text/plain, so
 * it falls through to us. So does an image file copied from the OS file manager.
 *
 * Returns [] whenever the paste is not ours — callers must treat an empty array as
 * "do not preventDefault".
 */
export function filesFromClipboard(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return [];

  let files: File[] = Array.from(dt.files ?? []);

  // Some engines expose a pasted bitmap only through `items`, never populating `files`.
  // Checked second so the simple path stays the fast path.
  if (files.length === 0 && dt.items) {
    files = Array.from(dt.items)
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
  }

  if (files.length === 0) return [];

  // Rule 2. Wrapped because getData can throw on a locked-down clipboard; a throw here must not
  // cost the user their paste, and treating it as "no text" simply means we handle the files,
  // which is the safe reading when files are demonstrably present.
  let text = '';
  try {
    text = dt.getData('text/plain') ?? '';
  } catch {
    text = '';
  }
  if (text.trim().length > 0) return [];

  return files;
}
